import {initBirdie, updateBirdie} from '@birdie-so/snippet'
import type {BirdieContact} from '@birdie-so/snippet'
import type {FC} from 'react'
import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useUserContext} from '@containers/App/UserProvider'

// Public Birdie client id. It ships in the browser (injected CDN snippet), so it
// is NOT a secret. Overridable per-environment via VITE_APP_BIRDIE_CLIENT_ID.
const BIRDIE_CLIENT_ID = import.meta.env.VITE_APP_BIRDIE_CLIENT_ID || 'eehtdt1y'

// Birdie is disabled by default. Set VITE_APP_ENABLE_BIRDIE=true to enable it
// (mirrors the VITE_APP_ENABLE_USAGE_TRACKING opt-in pattern).
const isBirdieEnabled = (): boolean => import.meta.env.VITE_APP_ENABLE_BIRDIE === 'true'

/**
 * Builds the Birdie contact from the authenticated user. Birdie requires a
 * contact (specifically an email) to capture console logs and network requests.
 * Returns null when no identifiable user is loaded yet.
 */
const buildContact = (
  user: {id: number; email: string; first_name: string; last_name: string} | null | undefined,
): BirdieContact | null => {
  if (!user?.email) {
    return null
  }
  return {
    id: String(user.id),
    email: user.email,
    name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
  }
}

/**
 * Headless component that boots the Birdie screen-recording snippet once and
 * keeps its contact/metadata in sync with the authenticated user and route.
 *
 * Wiring the contact is what makes support recordings carry console logs and
 * network requests (see Birdie Settings -> Logs). This complements, and does
 * not replace, the Intercom "Screen Recording by Birdie" integration.
 */
const BirdieInitializer: FC = (): null => {
  const userContext = useUserContext()
  const currentUser = userContext?.currentUser ?? null
  const location = useLocation()
  const initializedRef = useRef(false)
  // Track the last contact email we pushed to Birdie so we only update on change.
  const lastContactEmailRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isBirdieEnabled()) {
      return
    }

    const contact = buildContact(currentUser)
    const tenantId = currentUser?.userprofile?.current_tenant
    const metadata = {tenant_id: tenantId, route: location.pathname}

    // Initialize the snippet exactly once. If the user is not known yet we still
    // init with the clientId, then push the contact via updateBirdie once it
    // arrives. Re-running this effect after init only updates runtime data.
    if (!initializedRef.current) {
      initBirdie({
        clientId: BIRDIE_CLIENT_ID,
        ...(contact ? {contact} : {}),
        metadata,
      })
      initializedRef.current = true
      lastContactEmailRef.current = contact?.email ?? null
      return
    }

    // After init: keep contact + metadata current (handles login, user change,
    // and route changes) without re-initializing.
    if (contact && contact.email !== lastContactEmailRef.current) {
      updateBirdie({contact, metadata})
      lastContactEmailRef.current = contact.email
    } else {
      updateBirdie({metadata})
    }
  }, [currentUser, location.pathname])

  return null
}

export default BirdieInitializer
